// https://digital.vfc.com/wiki/display/ECOM15/Forms%3A+Login
// https://digital.vfc.com/wiki/display/ECOM15/Forms%3A+Registration
import type { LookupData } from '#root/api/clients/consumer/data-contracts'
import type { SocialLoginProvider } from '#types/featureFlags'
import type {
  AccountGatewayEntryPoint,
  AccountSetupActionOptionalStep,
  AccountSetupDecision,
  FlowTypes,
  FormLocation,
  FormMethods,
  MarketingSelection
} from '#types/gtm'

type ApiLoginMethod = LookupData['authMethods'][number]
type TrackingLoginMethod = 'OTP' | ApiLoginMethod
type SocialLoginMethod = Capitalize<SocialLoginProvider['type']>

type Interaction = 'Impression' | 'Email' | 'Select Email' | 'Submit' | 'Back' | 'Close' | 'Confirmed' | 'Setup Account' | 'ResendCode' | 'Error'
type GatewayEntryPoint = `Entry Point: ${AccountGatewayEntryPoint}`
type MarketingInteraction = 'MarketingOptIn Impression' | 'MarketingOptIn Complete' | 'MarketingOptIn Exit'
type SocialLoginInteraction = `Select ${SocialLoginMethod}`
type LoginMethodInteraction = `Sign in With ${FormMethods}`
type AuthAction = 'Registration' | 'Login' | 'Automatic Login' | 'Gateway'
type AccountSetupConfirmationAction = 'Setup:yesPoints' | 'Setup:noPoints'

const getBaseEvent = (
  eventAction: AuthAction | AccountSetupActionOptionalStep | AccountSetupConfirmationAction,
  eventLabel:
    Interaction
    | GatewayEntryPoint
    | LoginMethodInteraction
    | SocialLoginInteraction
    | AccountSetupDecision
    | MarketingInteraction,
  nonInteractive: boolean
) => ({
  event: 'loadEventData',
  eventCategory: 'Account',
  eventAction,
  eventLabel,
  nonInteractive,
  _clear: true
})

const mapLoginMethod = (method: ApiLoginMethod): FormMethods => {
  switch (method) {
    case 'mfa':
      return 'OTP'
    case 'password':
      return 'Standard'
    default:
      return capitalize(method) as FormMethods
  }
}

export default {
  onImpression: (eventAction: AuthAction, formLocation: FormLocation) => ({
    ...getBaseEvent(eventAction, 'Impression', true),
    customMetrics: {
      elementView: 1
    },
    customVariables: {
      formLocation
    }
  }),

  onEmail: (eventAction: AuthAction, formLocation: FormLocation, selection = false) => ({
    ...getBaseEvent(eventAction, selection ? 'Select Email' : 'Email', true),
    customMetrics: {
      elementView: 1
    },
    customVariables: {
      formLocation
    }
  }),

  onEntry: (entryPoint: AccountGatewayEntryPoint, formLocation: FormLocation) => ({
    ...getBaseEvent('Gateway', `Entry Point: ${entryPoint}`, true),
    customMetrics: {
      elementView: 1
    },
    customVariables: {
      formLocation
    }
  }),

  onMarketingImpression: (
    eventAction: AuthAction,
    formMethod: TrackingLoginMethod,
    registrationFlow: FlowTypes,
    formLocation: FormLocation
  ) => ({
    ...getBaseEvent(eventAction, 'MarketingOptIn Impression', true),
    customMetrics: {
      elementView: 1
    },
    customVariables: {
      formLocation,
      formMethod,
      registrationFlow,
    }
  }),

  onMarketingAction: (
    eventAction: AuthAction,
    eventLabel: Omit<MarketingInteraction, 'MarketingOptIn Impression'>,
    formMethod: TrackingLoginMethod,
    registrationFlow: FlowTypes,
    formLocation: FormLocation,
    marketingSelection?: MarketingSelection
  ) => ({
    ...getBaseEvent(eventAction, eventLabel as MarketingInteraction, false),
    customMetrics: {
      elementClose: 1
    },
    customVariables: {
      formLocation,
      formMethod,
      registrationFlow,
      marketingSelection,
    }
  }),

  onSocialLogin: (eventAction: AuthAction, provider: SocialLoginProvider['type'], formLocation: FormLocation) => ({
    ...getBaseEvent(eventAction, `Select ${capitalize(provider)}` as SocialLoginInteraction, true),
    customMetrics: {},
    customVariables: {
      formLocation
    }
  }),

  onLoginOptionSelected: (eventAction: AuthAction, formMethod: ApiLoginMethod, formLocation: FormLocation) => ({
    ...getBaseEvent(eventAction, `Sign in With ${mapLoginMethod(formMethod)}` as LoginMethodInteraction, true),
    customMetrics: {},
    customVariables: {
      formLocation,
      formMethod: mapLoginMethod(formMethod)
    }
  }),

  onLoginSubmit: (formLocation: FormLocation, automaticLogin: boolean) => ({
    ...getBaseEvent('Login', 'Submit', false),
    eventAction: automaticLogin ? 'Automatic Login' : 'Login',
    customMetrics: {},
    customVariables: {
      formLocation
    }
  }),

  onLoginClose: (formLocation: FormLocation, eventAction: AuthAction = 'Login') => ({
    ...getBaseEvent(eventAction, 'Close', false),
    customMetrics: {
      elementClose: 1
    },
    customVariables: {
      formLocation
    }
  }),

  onLoginConfirmed: (formLocation: FormLocation, formMethod: FormMethods, automaticLogin: boolean) => ({
    ...getBaseEvent(automaticLogin ? 'Automatic Login' : 'Login', 'Confirmed', false),
    customMetrics: {
      elementConversion: 1
    },
    customVariables: {
      formLocation,
      formMethod
    }
  }),

  /* TNF specific: this event is not happening when a loyal registration occurs in a second step separately to the first registration */
  onRegistrationSubmit: (formLocation: FormLocation, registrationFlow: FlowTypes) => ({
    ...getBaseEvent('Registration', 'Submit', false),
    customMetrics: {},
    customVariables: {
      formLocation,
      registrationFlow
    }
  }),

  onRegistrationClose: (
    formLocation: FormLocation,
    registrationFlow: FlowTypes,
    eventLabel: Interaction = 'Close',
    formMethod?: FormMethods | SocialLoginMethod
  ) => ({
    ...getBaseEvent('Registration', eventLabel, false),
    customMetrics: {
      elementClose: 1
    },
    customVariables: {
      formLocation,
      formMethod,
      registrationFlow
    }
  }),

  onRegistrationConfirmed: (
    interaction: Interaction,
    formLocation: FormLocation,
    registrationFlow: FlowTypes,
    formMethod: FormMethods,
    loyaltySignUp: boolean
  ) => ({
    ...getBaseEvent('Registration', interaction, false),
    customMetrics: {
      elementConversion: 1
    },
    customVariables: {
      formLocation,
      formMethod,
      registrationFlow,
      loyaltySignUp: loyaltySignUp ? 1 : 0
    }
  }),

  onAuthFlowInteraction: (
    eventAction: AuthAction,
    eventLabel: Interaction,
    formLocation: FormLocation,
    formMethod?: FormMethods | SocialLoginMethod
  ) => ({
    ...getBaseEvent(eventAction, eventLabel, false),
    customMetrics: {
      elementClick: 1
    },
    customVariables: {
      formLocation,
      formMethod,
    },
  }),

  onLoyaltyStep: (
    action: AccountSetupActionOptionalStep,
    decision: AccountSetupDecision,
    formLocation: FormLocation
  ) => ({
    ...getBaseEvent(action, decision, false),
    customMetrics: {},
    customVariables: {
      formLocation,
      formMethod: 'Standard',
      registrationFlow: 'loyalty',
      loyaltySignUp: 1,
    }
  }),

  onLoyaltySubmit: (
    action: AccountSetupConfirmationAction,
    formLocation: FormLocation
  ) => ({
    ...getBaseEvent(action, 'Confirmed', false),
    customMetrics: {},
    customVariables: {
      formLocation,
      formMethod: 'Standard',
      registrationFlow: 'loyalty',
      loyaltySignUp: 1
    }
  })
}
